import React from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import { api } from '../lib/api'
import OrgLandingPageEditor from '../components/OrgLandingPageEditor'
import OrgLandingPageViewer from '../components/OrgLandingPageViewer'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import MenuGuideButton from '../components/MenuGuideButton'
import { PageHeader } from '../ui/PageLayout'

const ORG_HOMEPAGE_ADMIN_ROLES = [
  Roles.OPERATOR,
  Roles.GA_MASTER_ADMIN,
  Roles.GA_MASTER_DEPUTY_ADMIN,
  Roles.GA_OTHER_ADMIN,
  Roles.GA_LEADER,
  Roles.GA_DEPUTY_LEADER,
  Roles.GA_MANAGER,
  Roles.GA_BRANCH_MANAGER,
  Roles.GA_DEPUTY_BRANCH_MANAGER,
  Roles.INSURER_MASTER_ADMIN,
  Roles.INSURER_MASTER_DEPUTY_ADMIN,
  Roles.INSURER_OTHER_ADMIN,
  Roles.INSURER_LEADER,
  Roles.INSURER_DEPUTY_LEADER,
  Roles.INSURER_MANAGER,
  Roles.INSURER_HEAD_MANAGER,
  Roles.INSURER_BRANCH_MANAGER,
  Roles.INSURER_DEPUTY_BRANCH_MANAGER,
]

export default function SalesOrgHomepagePage() {
  const { user, hasAnyRole } = useAuth()
  const orgId = user?.orgId ? Number(user.orgId) : null
  const orgName = user?.orgName || '내 조직'
  const canManage = Boolean(hasAnyRole?.(ORG_HOMEPAGE_ADMIN_ROLES))
  const [landingPage, setLandingPage] = React.useState(null)
  const [loading, setLoading] = React.useState(!canManage)
  const [error, setError] = React.useState('')

  React.useEffect(() => {
    if (canManage || !orgId) {
      setLoading(false)
      return
    }
    let ignore = false
    setLoading(true)
    setError('')
    api.get(`/orgs/${orgId}/landing-page`, { __silent: true })
      .then((res) => {
        if (ignore) return
        const data = res?.data?.data || res?.data
        setLandingPage(data || null)
      })
      .catch((err) => {
        if (ignore) return
        setError(err?.response?.data?.message || err?.message || '조직홈페이지 정보를 불러오지 못했습니다.')
      })
      .finally(() => {
        if (!ignore) setLoading(false)
      })
    return () => {
      ignore = true
    }
  }, [canManage, orgId])

  if (!orgId) {
    return (
      <div className="space-y-4">
        <PageHeader title="조직홈페이지" titleAccessory={<MenuGuideButton slug="org-homepage" title="조직홈페이지 가이드" />} description="소속 조직이 확인되지 않아 조직홈페이지를 불러올 수 없습니다." />
        <div className="rounded-3xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
          조직에 소속된 계정에서만 조직홈페이지를 관리할 수 있습니다.
        </div>
      </div>
    )
  }

  if (canManage) {
    return <OrgLandingPageEditor orgId={orgId} onUpdate={() => {}} selectedOrg={{ id: orgId, name: orgName }} />
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const publicUrl = origin ? `${origin}/orgs/${orgId}/landing` : `/orgs/${orgId}/landing`

  return (
    <div className="space-y-4">
      <PageHeader title="조직홈페이지" titleAccessory={<MenuGuideButton slug="org-homepage" title="조직홈페이지 가이드" />} description={`조직: ${orgName}`} />
      <div className="rounded-3xl border border-slate-200 bg-white px-5 py-4">
        <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          조직홈페이지의 초안 저장, 발행, 수정은 관리자만 가능합니다. 현재 계정은 미리보기만 확인할 수 있습니다.
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            공개 페이지 보기
          </a>
          <Link
            to="/sales/consultations"
            className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            DB수집관리 보기
          </Link>
        </div>
      </div>

      {loading ? <Loading /> : null}
      {!loading && error ? <InlineError message={error} /> : null}
      {!loading && !error && landingPage ? (
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
          <OrgLandingPageViewer landingPageData={landingPage} orgName={orgName} isPreview />
        </div>
      ) : null}
      {!loading && !error && !landingPage ? (
        <div className="rounded-3xl border border-slate-200 bg-white px-5 py-10 text-center text-sm text-slate-500">
          아직 조직홈페이지 초안이 생성되지 않았습니다.
        </div>
      ) : null}
    </div>
  )
}
